import type { components } from '@adiox/api';

type EquityPoint = components['schemas']['EquityPoint'];

const W = 720;
const H = 220;
const PAD = { left: 56, right: 12, top: 12, bottom: 24 };

/**
 * The balance and equity at each daily rollover, as two lines. The figures are the server's; this only draws them. A short
 * text summary and the full table stand beside the picture, so it can be read without seeing it.
 */
export function EquityChart({ points, currency }: { points: EquityPoint[]; currency: string }) {
  if (points.length === 0) {
    return (
      <p className="p-4 text-sm text-muted">
        No daily snapshots in this range yet. They are taken at each rollover.
      </p>
    );
  }
  const values = points.flatMap((p) => [p.balance, p.equity]);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const x = (i: number) =>
    PAD.left +
    (points.length === 1
      ? (W - PAD.left - PAD.right) / 2
      : (i / (points.length - 1)) * (W - PAD.left - PAD.right));
  const y = (v: number) => PAD.top + (1 - (v - lo) / span) * (H - PAD.top - PAD.bottom);
  const line = (pick: (p: EquityPoint) => number) =>
    points.map((p, i) => `${x(i).toFixed(1)},${y(pick(p)).toFixed(1)}`).join(' ');
  const last = points[points.length - 1]!;
  const fmt = (v: number) =>
    v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="flex h-full flex-col gap-2 overflow-auto p-3">
      <p className="text-sm" data-testid="equity-summary">
        {points.length} day{points.length === 1 ? '' : 's'}: latest balance {fmt(last.balance)} {currency},
        equity {fmt(last.equity)} {currency}; equity ranged from{' '}
        {fmt(Math.min(...points.map((p) => p.equity)))} to {fmt(Math.max(...points.map((p) => p.equity)))}.
      </p>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Balance and equity by day"
        className="w-full max-w-3xl"
        data-testid="equity-chart"
      >
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(lo + span * f)}
              y2={y(lo + span * f)}
              stroke="var(--border)"
              strokeDasharray="3 3"
            />
            <text
              x={PAD.left - 6}
              y={y(lo + span * f) + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--muted)"
            >
              {fmt(lo + span * f)}
            </text>
          </g>
        ))}
        <polyline
          points={line((p) => p.balance)}
          fill="none"
          stroke="var(--muted)"
          strokeWidth="2"
          strokeDasharray="6 3"
        />
        <polyline points={line((p) => p.equity)} fill="none" stroke="var(--accent)" strokeWidth="2" />
        {points.length === 1 && <circle cx={x(0)} cy={y(points[0]!.equity)} r="4" fill="var(--accent)" />}
        <text x={PAD.left} y={H - 6} fontSize="11" fill="var(--muted)">
          {points[0]!.day}
        </text>
        <text x={W - PAD.right} y={H - 6} fontSize="11" textAnchor="end" fill="var(--muted)">
          {last.day}
        </text>
      </svg>
      <p className="flex gap-4 text-xs text-muted">
        <span>
          <span aria-hidden="true" className="mr-1 inline-block h-0.5 w-5 bg-accent align-middle" /> Equity
          (solid)
        </span>
        <span>
          <span
            aria-hidden="true"
            className="mr-1 inline-block h-0.5 w-5 border-t-2 border-dashed border-muted align-middle"
          />{' '}
          Balance (dashed)
        </span>
      </p>
      <details className="text-sm">
        <summary className="cursor-pointer text-muted">Show the figures</summary>
        <table className="mt-2 w-full max-w-md" aria-label="Daily balance and equity">
          <thead>
            <tr className="text-left text-xs uppercase text-muted">
              <th className="py-1">Day</th>
              <th className="py-1 text-right">Balance</th>
              <th className="py-1 text-right">Equity</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.day} className="border-t border-line/40">
                <td className="num py-0.5">{p.day}</td>
                <td className="num py-0.5 text-right">{fmt(p.balance)}</td>
                <td className="num py-0.5 text-right">{fmt(p.equity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
